/**
 * orgToday — "what day is it, for the organization?"
 *
 * Every calendar view RENDERS in the organization's timezone but used to decide
 * what "today" is from the browser's clock (`new Date()`), then format it with
 * local getters. The two disagree for as long as the offset between the viewer
 * and the org: a Kyiv viewer (UTC+3) on a New York org (UTC−4) is seven hours
 * ahead, so between 00:00 and 07:00 the browser is already on the next date
 * while the organization is not. That shifted the highlighted day, and on a
 * Sat→Sun or month boundary the "Today" button opened the wrong week/month
 * outright. `WeekGrid` already computed the highlight correctly, so a single
 * page could disagree with itself.
 *
 * `orgTodayDate` deliberately returns a BROWSER-LOCAL `Date` carrying the
 * ORGANIZATION's calendar components. That keeps every existing local-date
 * helper (`formatIsoDate`, `startOfWeek`, `Date.UTC(y, m, d)` bridges) working
 * untouched — the fix is a one-line swap at each call site rather than a
 * rewrite of the date algebra around it.
 *
 * Uses `Intl.formatToParts` rather than an offset calculation: it is
 * DST-correct by construction and needs no dependency on a view module.
 *
 * @module orgToday
 */

const pad2 = value => String(value).padStart(2, '0');

/**
 * The organization's current calendar date as `{ year, month, day }`, where
 * `month` is 1-based. Falls back to the browser's own date when the timezone is
 * unusable (bad name, `Intl` missing) — never throws, because a calendar that
 * renders on the wrong day still beats one that does not render.
 *
 * @param {?string} tz IANA timezone name
 * @param {Date} [now] injectable clock
 * @returns {{year: number, month: number, day: number}}
 */
export function orgTodayParts(tz, now = new Date()) {
	const browser = { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
	if (!tz || typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function')
		return browser;

	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: tz,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		}).formatToParts(now);

		const pick = type => {
			const found = parts.find(part => part.type === type);
			return found ? Number(found.value) : NaN;
		};
		const year = pick('year');
		const month = pick('month');
		const day = pick('day');
		if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return browser;
		return { year, month, day };
	} catch {
		return browser;
	}
}

/**
 * The organization's today as `YYYY-MM-DD`.
 *
 * @param {?string} tz IANA timezone name
 * @param {Date} [now] injectable clock
 * @returns {string}
 */
export function orgTodayKey(tz, now = new Date()) {
	const { year, month, day } = orgTodayParts(tz, now);
	return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * The organization's today as a browser-local `Date` at midnight — a drop-in
 * replacement for `new Date()` wherever a view is deciding which day, week or
 * month to show. See the module note on why the returned Date is local.
 *
 * @param {?string} tz IANA timezone name
 * @param {Date} [now] injectable clock
 * @returns {Date}
 */
export function orgTodayDate(tz, now = new Date()) {
	const { year, month, day } = orgTodayParts(tz, now);
	return new Date(year, month - 1, day);
}

export default orgTodayDate;
